import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
	AlertVariant,
	Card,
	CardBody,
	CardHeader,
	Flex,
	FlexItem,
	Grid,
	GridItem,
	Text,
} from '@patternfly/react-core';
import { Dashboard } from '../../../api/dashbboards/Dashboards';
import { Widget } from '../../../api/dashbboards/DashboardWidgets';
import { useMount } from 'react-use';
import DropGrid from '../../../components/dnd/DropGrid';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { OutletContext } from '../../../layout/Layout';
import PageTitleSubheader from '../../../layout/subheader/PageTitleSubheader';
import Loader from '../../../components/util/Loader';
import Authorized from '../../../components/security/Authorized';
import { Permission } from '../../../enums/permission.enum';
import './DashboardView.scss';
import { TNewDateRange } from '../../../api/types/TNewDateRange';
import { addNewRecentDashboard } from '../../../helpers/helper-components/recents-factory-helper';
import DashboardFilters from './DashboardFilters';
import { DashboardFilter } from '../../../api/dashbboards/DashboardFilter';
import EntityMiscButtons from '../../../helpers/helper-components/EntityMiscButtons';
import { useApplication, useUser } from '../../../components/user/ApplicationProvider';
import {
	SharedEntity,
	TDashboardInightsEntity,
	TSharedEntity,
} from '../../../api/shared-entity/SharedEntity';
import { Favorites, TFavorites } from '../../../api/favorites/Favorites';
import { FavoriteTypes } from '../../../enums/favorite-types';
import UserSecureFiltersIcon from '../../../components/user-secure-filters-icon/UserSecureFiltersIcon';
import { getExtendedPrompt } from '../../../api/zone/getExtendedPrompt';
import { useCommonStoreContext } from '../../../components/common-store/CommonStoreProvider';
import { InsightsActionsResponse } from '../../../api/types/insights-actions-response';
import InsightsDashboardPanel from '../../../components/data-builder/InsightsDashboardSidebar';
import {
	DataframeDataRetrievalRequest,
	DataframeDataRetrievalResponse,
	InsightsActionsRequest,
	TDataframe,
	TDateRange,
	TReport,
} from '../../../api/types';
import { setAvailableMeasuresAndDimensionsByDataframe } from '../../../hooks/DataBuilderHooks';
import { Dataframe, TNewDataframeFilter } from '../../../api/dataframes/Dataframes';
import { OptionsBuilderItemTypes } from '../../../types/dataframes/options-builder-item-types';
import {
	formatCsvDataToColumns,
	MultipartResponse,
} from '../../../helpers/multipart-response.helper';
import { DataBuilderTypes } from '../../../enums/data-builder-types';
import { InsightsActions } from '../../../api/lambda/InsightsActions';
import { DateRange } from '../../../api/date-period-selector/DateRange';
import { timestampToMMDDYYYY } from '../../../utilities';
import { PeriodTypesEnum } from '../../../enums/period-types-enum';
import { TChart } from '../../../api/charts/Chart';
import { AxiosError } from 'axios';
import { LambdaGraphSummariesResponse } from '../../../api/types/lambda-graph-summaries-response';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const retryWithBackoff = async <T,>(
	fn: () => Promise<T>,
	maxRetries = 3,
	baseDelayMs = 1000
): Promise<T> => {
	let lastError: Error | null = null;
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			return await fn();
		} catch (error) {
			lastError = error as Error;
			if (attempt < maxRetries - 1) {
				const delayMs = baseDelayMs * Math.pow(2, attempt);
				console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms`);
				await delay(delayMs);
			}
		}
	}
	throw lastError;
};

const DashboardView = () => {
	const { dashboardId } = useParams();
	const navigate = useNavigate();
	const { addToast } = useToast();
	const [loading, setLoading] = useState<boolean>(false);
	const [dashboardModel, setDashboardModel] = useState<Dashboard>({
		...Dashboard.Default(),
	});
	const [selectedDate, setSelectedDate] = useState<TNewDateRange>();
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [dashboardFilters, setDashboardFilters] = useState<DashboardFilter[]>();
	const currentUser = useUser();
	const [favorite, setFavorite] = useState<TFavorites>();
	const { setCurrentSelectedDashboard } = useCommonStoreContext();
	const [isEmptyDashboardFilters, setIsEmptyDashboardFilters] = useState<boolean>();
	const [widgets, setWidgets] = useState<Widget[]>([]);
	const [selectedChartWidget, setSelectedChartWidget] = useState<Widget>();
	const [chatPrefill, setChatPrefill] = useState<string | undefined>();

	const updateSelectedDate = useCallback(
		(startDateRange: TNewDateRange, endDateRange: TNewDateRange) => {
			if (setSelectedDate) {
				setSelectedDate({
					begin_date: startDateRange.begin_date,
					end_date: endDateRange.end_date,
					period: startDateRange.period,
					sequence: startDateRange.sequence,
				});
			}
		},
		[selectedDate, setSelectedDate]
	);
	const [isInsightsOpen, setIsInsightsOpen] = useState<boolean>(false);
	const [insightsActions, setInsightsActions] = useState<
		{ entityId: number; insightsResponse: InsightsActionsResponse }[]
	>([]);

	const { currentDatePeriods, dimensions, measures, zoneSettings } = useApplication();
	const defaultDatePeriod =
		currentDatePeriods.find((dp) => dp.period === 3) ?? (DateRange.Default() as TDateRange);

	const previousSelectedStartPeriod = localStorage.getItem('currentSelectedStartPeriod');
	const previousSelectedEndPeriod = localStorage.getItem('currentSelectedEndPeriod');
	const hasPreviouslySelectedPeriod =
		previousSelectedStartPeriod != null || previousSelectedEndPeriod != null;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const previouslySelectedStartDate: TDateRange | null =
		hasPreviouslySelectedPeriod && previousSelectedStartPeriod
			? JSON.parse(previousSelectedStartPeriod)
			: null;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const previouslySelectedEndDate: TDateRange | null =
		hasPreviouslySelectedPeriod && previousSelectedEndPeriod
			? JSON.parse(previousSelectedEndPeriod)
			: null;

	const { user } = useApplication();
	const [insightEntities, setInsightEntities] = useState<TDashboardInightsEntity[]>([]);
	const { setCurrentGraphSummaryStats } = useCommonStoreContext();
	const [lambdaGraphHighlights, setLambdaGraphHighlights] = useState<
		LambdaGraphSummariesResponse[]
	>([]);
	const [dashboardSummaryText, setDashboardSummaryText] = useState<string>();

	const getPageData = () => {
		if (dashboardId) {
			setLoading(true);
			Dashboard.Get(parseInt(dashboardId), ['sharedDashboards'])
				.then((dashboardResponse) => {
					if (dashboardResponse) {
						setDashboardModel({ ...dashboardResponse });
						setCurrentSelectedDashboard(dashboardResponse);
						Widget.GetAll({ dashboard: dashboardId }, ['conditionalRules'])
							.then((widgetsResponse) => {
								setWidgets(
									widgetsResponse.sort((a, b) => a.row - b.row || a.col - b.col)
								);
								setLoading(false);
							})
							.catch(() => {
								addToast('Get all widgets failed.', AlertVariant.danger);
							});
					}
				})
				.catch(() => {
					addToast('Get dashboard failed.', AlertVariant.danger);
				});

			getFavoriteData();

			void SharedEntity.GetAllByDashboard(+dashboardId).then((response) => {
				setInsightEntities(response);
			});
		}
	};

	useMount(() => {
		getPageData();
		if (dashboardId) {
			//log entry into recents for loaded dashboard
			addNewRecentDashboard(dashboardId);

			setCurrentGraphSummaryStats([]);
		}

		// Check for #chat= hash param (from Discover page)
		const hash = window.location.hash;
		if (hash.startsWith('#chat=')) {
			const question = decodeURIComponent(hash.slice('#chat='.length));
			setChatPrefill(question);
			setIsInsightsOpen(true);
			window.history.replaceState(
				null,
				'',
				window.location.pathname + window.location.search
			);
		} else {
			setIsInsightsOpen(false);
		}
	});

	useEffect(() => {
		getPageData();
	}, [window.location.pathname]);

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle={''}
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
					isCollapsable={false}
				/>
			),
			hideLeftSideBar: true,
		});
	}, [setSubSide, subNavExpanded, setSubNavExpanded]);

	const getDashboardSharedPermissions = () => {
		let userSharedRecord: { canEdit: boolean; canShare: boolean } = {
			canEdit: false,
			canShare: false,
		};
		if (!dashboardModel || currentUser.id === dashboardModel?.owner) {
			userSharedRecord = {
				canEdit: true,
				canShare: true,
			};
		} else {
			dashboardModel?.sharedDashboards?.map((sd) => {
				const thisUser = (sd as TSharedEntity).shared_entity_users?.find(
					(u) => u.user === currentUser.id
				);
				const thisGroup = (sd as TSharedEntity).shared_entity_groups?.find(
					(u) => currentUser.groups.findIndex((ug) => ug === u.group) > -1
				);
				if (thisUser) {
					userSharedRecord = { canEdit: thisUser.can_edit, canShare: thisUser.can_share };
				} else if (thisGroup) {
					userSharedRecord = {
						canEdit: thisGroup.can_edit,
						canShare: thisGroup.can_share,
					};
				}
			});
		}

		return userSharedRecord;
	};

	const getFavoriteData = () => {
		Favorites.Get(+(dashboardId ?? 0), FavoriteTypes.dashboard)
			.then((response: TFavorites) => {
				if (response) {
					setFavorite(response);
				} else {
					setFavorite({
						owner: currentUser.id,
						entity_id: +(dashboardId ?? 0),
						entity_type: FavoriteTypes.dashboard,
						name: dashboardModel?.name,
					});
				}
			})
			.catch(() => {
				addToast(
					'There was an issue setting the dashboard as a favorite',
					AlertVariant.danger
				);
			});
	};

	const updateFilters = (filters: DashboardFilter[]) => {
		setDashboardFilters(filters);
	};

	const mapFilters = (
		filters: DashboardFilter[],
		dataframe: TDataframe
	): TNewDataframeFilter[] => {
		const filtersToReturn: TNewDataframeFilter[] = [];
		const availableItems = setAvailableMeasuresAndDimensionsByDataframe(
			dataframe,
			undefined, //parentDataframe ?? undefined,
			measures,
			dimensions,
			false
		);

		if (filters.length) {
			const allowedFilters = filters
				? filters.filter((item) => {
						if (item.entity_type === OptionsBuilderItemTypes.Dimension) {
							const dimIds = availableItems.dimensions.map((dim) => dim.id);
							return item.value && dimIds.includes(item.entity_id);
						} else {
							const attributes = availableItems.dimensions.flatMap(
								(dim) => dim.dimensionAttributes
							);
							const attributeIds = attributes.map((attr) => attr.id);
							return item.value && attributeIds.includes(item.entity_id);
						}
				  })
				: [];

			allowedFilters.forEach((filter) => {
				const existingFilter = dataframe.filters.find(
					(df) =>
						df.entity_id === filter.entity_id && df.entity_type === filter.entity_type
				);

				if (existingFilter && filter.value) {
					existingFilter.excluded = filter.excluded;
					existingFilter.isExistingValue = filter.isExistingValue;
					existingFilter.operator = filter.operator;
					existingFilter.value = filter.value;
				} else {
					filtersToReturn.push({
						entity_id: filter.entity_id,
						entity_type: filter.entity_type,
						excluded: filter.excluded,
						isExistingValue: filter.isExistingValue,
						operator: filter.operator,
						value: filter.value,
					});
				}
			});
		}

		return [...filtersToReturn, ...dataframe.filters];
	};

	const getPeriodData = (date: { begin_date: number; end_date: number; period: number }) => {
		const response = {
			time_period: '',
			time_range: {
				start_date: '',
				end_date: '',
			},
		};

		if (date) {
			response.time_range.start_date = timestampToMMDDYYYY(date.begin_date);
			response.time_range.end_date = timestampToMMDDYYYY(date.end_date);

			switch (date.period) {
				case 1: {
					response.time_period = PeriodTypesEnum.Year;
					break;
				}
				case 2: {
					response.time_period = PeriodTypesEnum.Quarter;
					break;
				}
				case 3: {
					response.time_period = PeriodTypesEnum.Month;
					break;
				}
				case 4: {
					response.time_period = PeriodTypesEnum.Week;
					break;
				}
				default: {
					response.time_period = PeriodTypesEnum.Month;
					break;
				}
			}
		}

		return response;
	};

	const getInsightsData = (
		dataframe: TDataframe,
		chartRetrevialCSVData: MultipartResponse<DataframeDataRetrievalResponse> | undefined,
		csvDataUnformatted: string,
		currentEntity: TChart | TReport,
		entityType: DataBuilderTypes
	) => {
		if (dataframe && chartRetrevialCSVData) {
			const previousDate = {
				begin_date:
					hasPreviouslySelectedPeriod && previouslySelectedStartDate
						? previouslySelectedStartDate?.begin_date
						: defaultDatePeriod?.begin_date ?? 0,
				end_date:
					hasPreviouslySelectedPeriod && previouslySelectedEndDate
						? previouslySelectedEndDate.end_date
						: defaultDatePeriod?.end_date ?? 0,
				period:
					hasPreviouslySelectedPeriod && previouslySelectedStartDate
						? previouslySelectedStartDate.period
						: defaultDatePeriod?.period ?? 0,
			};
			const date = selectedDate ?? previousDate;

			const request: InsightsActionsRequest = {
				metadata: {
					// eslint-disable-next-line no-unsafe-optional-chaining
					key_measures: (dataframe?.datasets).map((fact) => {
						return {
							name: fact.title ?? '',
							description: '',
						};
					}),
					dimensions: dataframe?.rowEntry.map((dim) => {
						return {
							name: dim.title ?? '',
							description: '',
						};
					}),
					columns: chartRetrevialCSVData.json?.columns ?? [],
					...getPeriodData(date),
					filters_applied: getInsightsFilters(dataframe),
					extended_prompt: getExtendedPrompt(zoneSettings),
				},
				data: csvDataUnformatted,
				logging: {
					user_id: currentUser.id,
					entity_type: entityType,
					entity_id: currentEntity?.id ?? 0,
				},
			};
			retryWithBackoff(() => InsightsActions.GetInsights(request, true), 3, 1000)
				.then((response) => {
					if (response.success) {
						if (typeof response.data === 'string') {
							response.data = JSON.parse(response.data) as InsightsActionsResponse;
						}
						const responseFormatted = {
							entityId: currentEntity.id ?? 0,
							insightsResponse: response.data,
						};
						setInsightsActions((prev) => [...prev, responseFormatted]);
					} else {
						console.log(
							`InsightsActions GetInsights returned 200 but returned following message: ${response.message}`
						);
					}
				})
				.catch((error: AxiosError): void => {
					console.error('InsightsActions GetInsights failed after retries:', {
						message: error.message,
						code: error.code,
						status: error.response?.status,
						isNetworkError: !error.response,
					});
				});
		}
	};

	const getInsightsFilters = (dataframe: TDataframe) => {
		const filters: string[] = [];
		if (dataframe) {
			dataframe.filters.forEach((filter) => {
				if (filter.entity_type === OptionsBuilderItemTypes.DimensionAttribute) {
					const dim = dimensions.find((dim) =>
						dim.dimensionAttributes.some((dimAttr) => dimAttr.id === filter.entity_id)
					);
					if (dim) {
						const dimAttr = dim.dimensionAttributes.find(
							(dimAttr) => dimAttr.id === filter.entity_id
						);

						if (dimAttr) {
							filters.push(`${dimAttr.name ?? ''}: ${filter.value ?? ''}`);
						}
					}
				} else if (filter.entity_type === OptionsBuilderItemTypes.Dimension) {
					const dim = dimensions.find((dim) => dim.id === filter.entity_id);

					if (dim) {
						filters.push(`${dim.name ?? ''}: ${filter.value ?? ''}`);
					}
				}
			});
		}

		return filters;
	};

	/** Fetch insights for a single widget (called from sidebar "Analyze" button) */
	const beginInsightsForWidget = (widget: Widget) => {
		const currentChartObject = insightEntities.find((y) => y.chart?.id === widget.chart);
		const currentTableObject = insightEntities.find((y) => y.report?.id === widget.report);

		if (!currentChartObject && !currentTableObject) return;

		const doFetch = async () => {
			if (currentChartObject)
				await fetchDataForChart(currentChartObject.chart!, currentChartObject.dataframe!);
			if (currentTableObject)
				await fetchDataForTable(currentTableObject.report!, currentTableObject.dataframe!);
		};

		doFetch().catch((error) => {
			console.error('Error retrieving insights data for widget:', error);
		});
	};

	const fetchDataForChart = async (currentChart: TChart, dataframe: TDataframe) => {
		try {
			// 1. Prepare request data and set local variables to track it
			const requestDatasets = dataframe.datasets;
			const requestRowEntry = dataframe.rowEntry;

			const request: DataframeDataRetrievalRequest = {
				dataframeId: dataframe.id,
				begin_date: selectedDate?.begin_date ?? 0,
				end_date: selectedDate?.end_date ?? 0,
				periodId: selectedDate?.period ?? 0,
				override: {
					id: dataframe.id,
					...(dataframe.parent && { parent: dataframe.parent }),
					columnEntry: [],
					datasets: requestDatasets,
					name: 'override',
					rowEntry: requestRowEntry,
					order: dataframe.order,
					filters: mapFilters(dashboardFilters ?? [], dataframe),
				},
			};

			// 2. Trigger further data fetching with the formatted data
			const newDataResponse = await Dataframe.Retrieve(request);
			const newResponseRetrievalData = formatCsvDataToColumns(
				newDataResponse.json?.columns ?? [],
				newDataResponse.csvData ?? [],
				request?.periodId ?? 3
			) as MultipartResponse<DataframeDataRetrievalResponse>;

			// 3. Now, pass the data to your insights data handler
			getInsightsData(
				dataframe,
				newResponseRetrievalData,
				newDataResponse.csvDataUnformatted ?? '',
				currentChart,
				DataBuilderTypes.chart
			);
		} catch (error) {
			const err = error as Error;
			console.log('Error in retrieving insights data: ' + err.message);
		}
	};

	const fetchDataForTable = async (currentTable: TReport, dataframe: TDataframe) => {
		try {
			// 1. Prepare request data and set local variables to track it
			const requestDatasets = dataframe.datasets;
			const requestRowEntry = dataframe.rowEntry;

			const request: DataframeDataRetrievalRequest = {
				dataframeId: dataframe.id,
				begin_date: selectedDate?.begin_date ?? 0,
				end_date: selectedDate?.end_date ?? 0,
				periodId: selectedDate?.period ?? 0,
				override: {
					id: dataframe.id,
					...(dataframe.parent && { parent: dataframe.parent }),
					columnEntry: [],
					datasets: requestDatasets,
					name: 'override',
					rowEntry: requestRowEntry,
					order: dataframe.order,
					filters: mapFilters(dashboardFilters ?? [], dataframe),
				},
			};

			// 2. Trigger further data fetching with the formatted data
			const newDataResponse = await Dataframe.Retrieve(request);
			const newResponseRetrievalData = formatCsvDataToColumns(
				newDataResponse.json?.columns ?? [],
				newDataResponse.csvData ?? [],
				request?.periodId ?? 3
			) as MultipartResponse<DataframeDataRetrievalResponse>;

			// 3. Now, pass the data to your insights data handler
			getInsightsData(
				dataframe,
				newResponseRetrievalData,
				newDataResponse.csvDataUnformatted ?? '',
				currentTable,
				DataBuilderTypes.table
			);
		} catch (error) {
			const err = error as Error;
			console.log('Error in retrieving insights data: ' + err.message);
		}
	};

	useEffect(() => {
		if (isInsightsOpen) {
			setSubSide((prev) => ({
				...prev,
				rightSideBar: (
					<InsightsDashboardPanel
						widgets={widgets}
						insightsActions={insightsActions}
						setInsightsActions={setInsightsActions}
						isInsightsOpen={isInsightsOpen}
						setIsInsightsOpen={setIsInsightsOpen}
						openDashboardInsightsPanel={openDashboardInsightsPanel}
						insightsEntities={insightEntities}
						dashboard={dashboardModel}
						dashboardSummaryText={dashboardSummaryText}
						setDashboardSummaryText={setDashboardSummaryText}
						lambdaGraphHighlights={lambdaGraphHighlights}
						setLambdaGraphHighlights={setLambdaGraphHighlights}
						beginInsightsForWidget={beginInsightsForWidget}
						onHighlightWidget={setSelectedChartWidget}
						chatPrefillText={chatPrefill}
					/>
				),
			}));
		}
	}, [insightsActions, isInsightsOpen, dashboardSummaryText, lambdaGraphHighlights, chatPrefill]);

	const openDashboardInsightsPanel = (isOpen: boolean) => {
		setSubSide((prev) => ({
			...prev,
			hideLeftSideBar: true,
			rightSideBar: isOpen ? (
				<InsightsDashboardPanel
					widgets={widgets}
					insightsActions={insightsActions}
					setInsightsActions={setInsightsActions}
					isInsightsOpen={isInsightsOpen}
					setIsInsightsOpen={setIsInsightsOpen}
					openDashboardInsightsPanel={openDashboardInsightsPanel}
					insightsEntities={insightEntities}
					dashboard={dashboardModel}
					dashboardSummaryText={dashboardSummaryText}
					setDashboardSummaryText={setDashboardSummaryText}
					lambdaGraphHighlights={lambdaGraphHighlights}
					setLambdaGraphHighlights={setLambdaGraphHighlights}
					beginInsightsForWidget={beginInsightsForWidget}
					onHighlightWidget={setSelectedChartWidget}
					chatPrefillText={chatPrefill}
				/>
			) : undefined,
		}));
	};

	if (loading) {
		return <Loader />;
	}

	return (
		<>
			<Authorized
				by={Permission.ViewDashboard}
				shouldRedirect
			>
				<Card className="dashboard-view-container">
					<CardHeader>
						<Flex flex={{ default: 'flex_1' }}>
							<FlexItem>
								<Flex>
									<FlexItem>
										<Text component="h1">{dashboardModel.name}</Text>
									</FlexItem>
									<FlexItem alignSelf={{ default: 'alignSelfCenter' }}>
										<UserSecureFiltersIcon />
									</FlexItem>
								</Flex>
							</FlexItem>
							<FlexItem align={{ default: 'alignRight' }}>
								<EntityMiscButtons
									entityType={'dashboard'}
									entityName={dashboardModel.name}
									canShare={getDashboardSharedPermissions().canShare}
									canEdit={getDashboardSharedPermissions().canEdit}
									favorite={favorite}
									setFavorite={getFavoriteData}
									isView={true}
									openInsights={() => {
										setIsInsightsOpen(!isInsightsOpen);
										openDashboardInsightsPanel(!isInsightsOpen);
										setInsightsActions([]);
										setSelectedChartWidget(undefined);
										setChatPrefill(undefined);
									}}
									isInsightsOpen={isInsightsOpen}
								/>
							</FlexItem>
						</Flex>

						<DashboardFilters
							dashboardId={dashboardModel.id ?? 0}
							updateFilters={updateFilters}
							setSelectedDate={updateSelectedDate}
							isView
							setIsEmptyDashboardFilters={setIsEmptyDashboardFilters}
						/>
					</CardHeader>
					<CardBody className="dashboard-scroll-container">
						<Grid hasGutter>
							<GridItem span={12}>
								{!dashboardFilters ? (
									<Loader />
								) : (
									<DropGrid
										displayOnly
										widgets={widgets}
										selectedDate={selectedDate}
										gridLayout={dashboardModel.hasLayout ?? false}
										gridBorder={dashboardModel.hasBorders ?? false}
										filters={dashboardFilters}
										allowClickNavigate
										isDashboard
										isEmptyDashboardFilters={isEmptyDashboardFilters}
										isInsightsOpen={isInsightsOpen}
										selectedChartWidget={selectedChartWidget}
										insightEntities={insightEntities}
									/>
								)}
							</GridItem>
						</Grid>
					</CardBody>
				</Card>
			</Authorized>
		</>
	);
};

export default DashboardView;
